/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable no-unused-vars */
import React, {Component} from 'react';
import {connect} from 'react-redux';
import LearnUtilsHome from './LearnUtils';
import {injectIntl} from 'react-intl';
import DiscoverAnalytics from '../../../utils/DiscoverAnalytics';

import {LearnApp, DiscoverUtils, LearnUtils} from 'discover-hub';

/* eslint-enable no-unused-vars */
import './Learn.scss';

const analytics = new DiscoverAnalytics();

interface Props {
	locale: string;
	env: string;
	match: any;
	intl: any;
	location: any;
	setOverwriteComponent: Function;
	onDiscoverPathChange: Function;
	optinInited: boolean;
	isDeeplyLinked: boolean | null | undefined;
	searchedApp: string | null | undefined;
	experienceLevel: string | null | undefined;
	history: any;
	pageIndex: number;
}

interface State {
	pageIndex: number;
	app: string;
}
class LearnDiscover extends Component<Props, State> {
	constructor(props) {
		super(props);

		this.state = {
			pageIndex: this.props.pageIndex,
			app: LearnDiscover.getApp(this.props.location),
		};
	}

	componentDidUpdate(prevProps) {
		if (prevProps.location !== this.props.location) {
			const nextApp = LearnDiscover.getApp(this.props.location);
			const nextPageIndex = LearnDiscover.getPageIndex(this.props.location);

			if (nextApp !== this.state.app) {
				// eslint-disable-next-line react/no-did-update-set-state
				this.setState({app: nextApp});
			}
			if (nextPageIndex !== this.state.pageIndex) {
				// eslint-disable-next-line react/no-did-update-set-state
				this.setState({pageIndex: nextPageIndex});
			}
		}
	}

	/**
	 * Given the page's path, extracts the page number and then turns it into an index.
	 * ex. page=2 --> 1
	 * @param {object} location
	 */
	static getPageIndex(location) {
		return DiscoverUtils.getSearchParams(location.search).page - 1 || 0;
	}

	static getApp(location) {
		const afterDiscover = location.pathname.split('/discover')[1];
		let nav
			= afterDiscover !== '' && afterDiscover.split('/').length > 0 ? afterDiscover.split('/')[3] : 'photoshop';
		nav = nav === '' ? 'photoshop' : nav;
		return nav;
	}

	static getNav(location) {
		const afterDiscover = location.pathname.split('/discover')[1];
		let nav
			= afterDiscover !== '' && afterDiscover.split('/').length > 0 ? afterDiscover.split('/')[1] : 'featured';
		nav = nav === '' ? 'featured' : nav;
		return nav;
	}

	/**
	 * [getPathData grabs the relveant learn fields from the url]
	 * @returns {[none]}
	 */
	getPathData() {
		const path = this.props.match.url.split('/');
		return {
			app: path[2],
		};
	}

	/**
	 * Callback function for Learn component when route changes.
	 *
	 * @param {Object} learnSignal
	 */
	onLearnPathChange(learnSignals) {
		let newPath = this.props.location.pathname;
		const newState: any = {};
		let isTutorial = false;

		const newSearchParams = learnSignals.reduce((params, signal) => {
			const signalValue = signal.value;

			if (signal.type === 'tutorial') {
				isTutorial = true;
				const beforeLearn = newPath.split('/learn')[0];
				const extraPath = `${signalValue.app}/${signalValue.level}/${signalValue.field}/${signalValue.playlistId}/${signalValue.tutorialId}`;
				newPath = `${beforeLearn}/learn/${extraPath}`;
			}
			else if (signal.type === 'app') {
				const beforeLearn = newPath.split('/learn')[0];
				let extraPath = `${signalValue.app}`;
				if (signalValue.level) {
					const queryParams = DiscoverUtils.getSearchPath({
						experienceLevel: signalValue.level,
					});

					extraPath = `${extraPath}/${queryParams}`;
				}
				newPath = `${beforeLearn}/learn/app/${extraPath}`;

				newState.app = signalValue.app;
				newState.pageIndex = this.state.pageIndex;
			}
			else if (signal.type === 'pageIndex') {
				// ..?page=1
				newState.pageIndex = signal.value;

				if (signal.value !== 0) {
					params.page = signal.value + 1;
				}
			}
			return params;
		}, {});

		if (isTutorial) {
			this.props.history.push({
				pathname: `${newPath}`,
			});
		}
		else {
			this.setState(newState); // TODO: put this back in later when
			// we actually have real server calls to re-render data
			this.props.history.push(`${newPath}${LearnUtils.getSearchPath(newSearchParams)}`);
		}
	}

	render() {
		if (!this.props.locale) {
			return null;
		}

		let locale = this.props.locale;
		if (!LearnUtils.isValidLearnLanguage(this.props.locale)) {
			// return (
			// 	<LearnLanguagePicker locale={this.props.locale}/>
			// );
			locale = 'en';
		}

		if (!this.props.optinInited) {
			return null;
		}

		// See if we need to deeplink the component.
		// const pathData = this.getPathData();

		return (
			<div className='learn-container-wrapper-discover'>
				<LearnApp
					env={this.props.env}
					imsData={LearnUtilsHome.getIMSData()}
					locale={locale}
					featureFlags={window.cchome.util.getAllFeatures()}
					categoryType={'learn'}
					selectedApp={this.state.app}
					location={this.props.location}
					pageIndex={this.state.pageIndex}
					onLearnPathChange={data => this.onLearnPathChange(data)}
					onDiscoverPathChange={this.props.onDiscoverPathChange}
					intl={this.props.intl}
					logger={window.cchome.util.logger}
					postAnalytics={data => analytics.postCustomData(data)}
					signedIn
				/>
			</div>
		);
	}
}

function mapStateToProps(state) {
	return {
		learnapps: state,
		optinInited: state.app.analyticsOptinInited,
	};
}

export default connect(mapStateToProps, {})(injectIntl(LearnDiscover));
